/** Trompos Space: interacción mínima del carrito Ride.
 * No modifica regalos, descuentos, checkout ni inventario; BOGOS los gestiona.
 * El contador solo se ejecuta con el cajón abierto, sin MutationObserver.
 */
(() => {
  'use strict';

  // Cronómetro visual: no reserva inventario ni vacía el carrito cuando finaliza.
  const STORAGE_KEY = 'tromposspace_cart_timer_end_v3';
  const DURATION_MS = 7 * 60 * 1000;
  let intervalId = null;
  let cachedEnd = null;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function readEnd() {
    if (cachedEnd !== null) return cachedEnd;
    try {
      cachedEnd = Number(localStorage.getItem(STORAGE_KEY)) || 0;
    } catch (_) {
      cachedEnd = 0;
    }
    return cachedEnd;
  }

  function writeEnd(value) {
    cachedEnd = value;
    try {
      if (value) localStorage.setItem(STORAGE_KEY, String(value));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* almacenamiento opcional */ }
  }

  function stopTimer() {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  function updateTimer() {
    const drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains('is-empty')) {
      writeEnd(0);
      stopTimer();
      return;
    }

    const clock = drawer.querySelector('[data-ts-clock]');
    if (!clock) return;

    let end = readEnd();
    // Iniciar solo la primera vez que se abre un carrito con productos.
    if (!end) {
      end = Date.now() + DURATION_MS;
      writeEnd(end);
    }
    const seconds = Math.max(0, Math.ceil((end - Date.now()) / 1000));
    const label = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    if (clock.textContent !== label) clock.textContent = label;
    const timerBox = clock.closest('.ts-space-cart__timer-box');
    timerBox?.classList.toggle('is-expired', seconds === 0);
    const caption = timerBox?.querySelector('[data-ts-timer-label]');
    if (caption) caption.textContent = seconds === 0 ? 'Cuenta atrás finalizada · puedes comprar' : 'Tu carrito · cuenta atrás';
    if (seconds === 0) stopTimer();
  }

  function startTimer() {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('active') || document.hidden) {
      stopTimer();
      return;
    }
    // No reiniciar al abrir/cerrar; el final se guarda como timestamp absoluto.
    updateTimer();
    if (intervalId === null && !drawer.classList.contains('is-empty') && readEnd() > Date.now()) {
      intervalId = window.setInterval(updateTimer, 1000);
    }
  }

  // API mínima para iniciar desde CartDrawer.open(), evitando depender solo de eventos.
  window.TSSpaceCartTimer = { start: startTimer, stop: stopTimer, refresh: updateTimer };

  document.addEventListener('ts-space-cart:open', startTimer);
  document.addEventListener('ts-space-cart:close', stopTimer);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopTimer();
    else startTimer();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.item_count === 0) {
        writeEnd(0);
        stopTimer();
      } else if (getDrawer()?.classList.contains('active')) {
        startTimer();
      }
    });
  }

  // Por si el script se ejecuta cuando el carrito ya estaba abierto.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startTimer, { once: true });
  } else {
    startTimer();
  }

  async function addSuggested(button) {
    const id = Number(button.dataset.tsAdd);
    if (!Number.isSafeInteger(id) || id <= 0 || button.disabled) return;

    button.disabled = true;
    const original = button.innerHTML;
    button.textContent = 'Añadiendo…';
    try {
      const drawer = getDrawer();
      if (!drawer) throw new Error('El carrito no está disponible.');
      const sections = drawer.getSectionsToRender().map((section) => section.id);
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status || !data.sections?.['cart-drawer']) {
        throw new Error(data.description || data.message || 'No se ha podido añadir el artículo.');
      }
      drawer.renderContents(data);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ts-space-cart', cartData: data, variantId: id });
      }
    } catch (error) {
      button.disabled = false;
      button.innerHTML = original;
      const alert = button.closest('.ts-space-cart__upsell')?.querySelector('.ts-space-cart__add-error');
      if (alert) {
        alert.textContent = error.message || 'No se ha podido añadir el artículo.';
        alert.hidden = false;
      } else {
        console.error(error);
      }
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-ts-add]');
    if (!button) return;
    event.preventDefault();
    addSuggested(button);
  });

  function parsePublicProperties(properties) {
    const result = {};
    if (!properties || typeof properties !== 'object') return result;
    Object.entries(properties).forEach(([key, value]) => {
      if (value == null || value === '' || String(key).startsWith('_')) return;
      result[key] = value;
    });
    return result;
  }

  function buildGroupId(item) {
    return [
      item.product_id || item.productId || item.product_id || '',
      item.variant_id || item.variantId || '',
      item.selling_plan_allocation?.selling_plan?.id || '',
      JSON.stringify(parsePublicProperties(item.properties || {})),
    ].join('|');
  }

  function groupCartItems(items) {
    const groups = new Map();
    (items || []).forEach((item, index) => {
      const groupId = buildGroupId(item);
      if (!groups.has(groupId)) groups.set(groupId, { id: groupId, items: [], rowIndexes: [] });
      groups.get(groupId).items.push(item);
      groups.get(groupId).rowIndexes.push(index);
    });
    return groups;
  }

  async function fetchCartJson() {
    const response = await fetch((window.routes?.cart_url || '/cart') + '.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error('No se ha podido leer el carrito.');
    return await response.json();
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
    } catch (_) {
      return value.toFixed(2).replace('.', ',') + ' €';
    }
  }

  function syncGroupedRows(cartData) {
    const drawer = getDrawer();
    if (!drawer) return;
    const rows = Array.from(drawer.querySelectorAll('#CartDrawer-CartItems .cart-item'));
    if (!rows.length || !Array.isArray(cartData?.items)) return;

    rows.forEach((row) => {
      row.classList.remove('ts-space-cart__merged-hidden');
      row.removeAttribute('data-ts-group-id');
      row.removeAttribute('data-ts-group-master');
      row.removeAttribute('aria-hidden');
      const input = row.querySelector('.quantity__input');
      if (input) {
        input.readOnly = false;
        input.removeAttribute('data-ts-group-id');
      }
      const removeButton = row.querySelector('cart-remove-button button');
      if (removeButton) removeButton.removeAttribute('data-ts-group-id');
    });

    const groups = groupCartItems(cartData.items);
    groups.forEach((group) => {
      if (group.items.length < 2) return;
      const primaryRow = rows[group.rowIndexes[0]];
      if (!primaryRow) return;

      const totalQty = group.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const totalOriginal = group.items.reduce((sum, item) => sum + Number(item.original_line_price || 0), 0);
      const totalFinal = group.items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
      const hasDiscount = totalOriginal !== totalFinal;

      primaryRow.dataset.tsGroupId = group.id;
      primaryRow.dataset.tsGroupMaster = 'true';

      const input = primaryRow.querySelector('.quantity__input');
      if (input) {
        input.value = String(totalQty);
        input.setAttribute('value', String(totalQty));
        input.dataset.tsGroupId = group.id;
        input.readOnly = true;
        input.dataset.cartQuantity = String(totalQty);
      }

      const priceWrapper = primaryRow.querySelector('.cart-item__price-wrapper');
      if (priceWrapper) {
        if (hasDiscount) {
          priceWrapper.innerHTML = '<div class="cart-item__discounted-prices">'
            + '<s class="cart-item__old-price price price--end">' + formatMoney(totalOriginal) + '</s>'
            + '<span class="price price--end">' + formatMoney(totalFinal) + '</span>'
            + '</div>';
        } else {
          priceWrapper.innerHTML = '<span class="price price--end">' + formatMoney(totalOriginal) + '</span>';
        }
      }

      const removeButton = primaryRow.querySelector('cart-remove-button button');
      if (removeButton) removeButton.dataset.tsGroupId = group.id;

      group.rowIndexes.slice(1).forEach((rowIndex) => {
        const row = rows[rowIndex];
        if (!row) return;
        row.classList.add('ts-space-cart__merged-hidden');
        row.setAttribute('aria-hidden', 'true');
      });
    });
  }

  let syncTimeout = null;
  function scheduleGroupedRowSync(delay = 60) {
    if (syncTimeout) window.clearTimeout(syncTimeout);
    syncTimeout = window.setTimeout(async () => {
      try {
        const cartData = await fetchCartJson();
        syncGroupedRows(cartData);
      } catch (error) {
        console.warn('[TS Space Cart] No se pudieron agrupar líneas duplicadas.', error);
      }
    }, delay);
  }

  async function updateGroupedQuantity(groupId, targetQty) {
    const nextQty = Math.max(0, Number(targetQty || 0));
    const cartData = await fetchCartJson();
    const groups = groupCartItems(cartData.items);
    const group = groups.get(groupId);
    if (!group || !group.items.length) return;

    const updates = {};
    group.items.forEach((item) => {
      updates[item.key] = 0;
    });
    if (nextQty > 0) {
      updates[group.items[0].key] = nextQty;
    }

    const drawer = getDrawer();
    const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [];
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'No se ha podido actualizar la cantidad.');
    }

    if (data.sections?.['cart-drawer'] && drawer) {
      drawer.renderContents(data);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ts-space-cart-grouped', cartData: data });
    }
    scheduleGroupedRowSync(40);
  }

  document.addEventListener('click', (event) => {
    const groupedRow = event.target.closest?.('#CartDrawer-CartItems .cart-item[data-ts-group-id]');
    if (!groupedRow) return;

    const removeButton = event.target.closest('cart-remove-button button');
    if (removeButton?.dataset.tsGroupId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      updateGroupedQuantity(removeButton.dataset.tsGroupId, 0).catch((error) => console.error(error));
      return;
    }

    const actionButton = event.target.closest('.quantity__button');
    if (!actionButton) return;
    const input = groupedRow.querySelector('.quantity__input[data-ts-group-id]');
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const current = Number(input.value || 0);
    const delta = actionButton.getAttribute('name') === 'plus' ? 1 : -1;
    updateGroupedQuantity(input.dataset.tsGroupId, current + delta).catch((error) => console.error(error));
  }, true);

  document.addEventListener('ts-space-cart:open', () => scheduleGroupedRowSync(20));
  document.addEventListener('DOMContentLoaded', () => scheduleGroupedRowSync(20));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      scheduleGroupedRowSync(80);
    });
  }

})();
